// src/domains/core/context-intelligence/adapters/healthAdapter.ts

import type {
  IndicatorValue,
  IndicatorDataQuery,
  IndicatorDataResponse,
} from "../types/indicators.types";

import {
  useOptimizedReadiness,
  useOptimizedActivity,
  useOptimizedSleep,
} from "@/domains/health/oura";

import { indicatorRegistry } from "../registry/indicatorRegistry";

// Import Oura hooks

// Note: Withings adapter will be implemented when Withings hooks are available
// For now, we'll create a placeholder structure

/**
 * Health Data Adapter
 * Adapts Health domain data (Oura, Withings) to unified Indicator format
 */
export const HealthAdapter = {
  /**
   * Get indicator data for a specific indicator
   * This is a static method that can be called from hooks
   *
   * @param indicatorId
   *
   * @param query
   *
   * @returns {Promise<IndicatorDataResponse | null>} Indicator data or null
   *
   */
  async getIndicatorData(
    indicatorId: string,
    query: IndicatorDataQuery,
  ): Promise<IndicatorDataResponse | null> {
    const indicator = indicatorRegistry.get(indicatorId);
    if (!indicator) {
      return null;
    }

    // Route to appropriate data source
    switch (indicator.source) {
      case "oura": {
        return HealthAdapter.getOuraData(indicatorId, query);
      }
      case "withings": {
        return HealthAdapter.getWithingsData(indicatorId, query);
      }
      default: {
        return null;
      }
    }
  },

  /**
   * Get Oura indicator data
   * Note: This is a simplified version - in production, this would need
   * to access historical data from Supabase or Oura API
   *
   * @param indicatorId
   *
   * @param query
   *
   * @returns {Promise<IndicatorDataResponse | null>} Oura indicator data or null
   *
   */
  getOuraData(
    indicatorId: string,
    query: IndicatorDataQuery,
  ): Promise<IndicatorDataResponse | null> {
    const indicator = indicatorRegistry.get(indicatorId);
    if (!indicator) {
      return Promise.resolve(null);
    }

    // For PoC, we'll return current values
    // In production, this would query historical data from Supabase
    const values: IndicatorValue[] = [];

    // This is a placeholder - actual implementation would:
    // 1. Query Supabase for historical Oura data
    // 2. Filter by date range
    // 3. Map to IndicatorValue format
    // 4. Apply aggregation if requested

    return Promise.resolve({
      indicator,
      metadata: {
        count: values.length,
        dateRange: { end: query.endDate, start: query.startDate },
        source: "oura",
      },
      values,
    });
  },

  /**
   * Get Withings indicator data
   * Placeholder for Withings integration
   *
   * @param indicatorId
   *
   * @param query
   *
   * @returns {Promise<IndicatorDataResponse | null>} Withings indicator data or null
   *
   */
  getWithingsData(
    indicatorId: string,
    query: IndicatorDataQuery,
  ): Promise<IndicatorDataResponse | null> {
    const indicator = indicatorRegistry.get(indicatorId);
    if (!indicator) {
      return Promise.resolve(null);
    }

    // Placeholder - to be implemented when Withings hooks are available
    const values: IndicatorValue[] = [];

    return Promise.resolve({
      indicator,
      metadata: {
        count: values.length,
        dateRange: { end: query.endDate, start: query.startDate },
        source: "withings",
      },
      values,
    });
  },

  /**
   * Get current value for an indicator (latest)
   * This is a convenience method for real-time data
   *
   * @param _indicatorId - Indicator ID (unused in placeholder implementation)
   *
   * @returns {null | number} Current indicator value or null
   *
   */
  getCurrentValue(_indicatorId: string): null | number {
    // Reason: This is a placeholder method that always returns null
    // Actual implementation would be in useIndicatorData hook
    return null;
  },
};

/**
 * React Hook for Health Adapter
 * Provides reactive access to health indicator data
 *
 * @param indicatorId
 *
 * @returns {object} Health indicator data with value, loading, error, and indicator
 *
 */
export function useHealthIndicatorData(indicatorId: string) {
  const indicator = indicatorRegistry.get(indicatorId);

  // Always call all hooks to comply with Rules of Hooks
  const readinessData = useOptimizedReadiness();
  const activityData = useOptimizedActivity();
  const sleepData = useOptimizedSleep();

  // Get appropriate data based on indicator
  let currentValue: null | number = null;
  let loading = false;
  let error: Error | null = null;

  if (indicator?.source === "oura") {
    switch (indicatorId) {
      case "oura_readiness": {
        currentValue = readinessData.score;
        loading = readinessData.loading;
        error = readinessData.error ? new Error(readinessData.error) : null;
        break;
      }
      case "oura_activity": {
        currentValue = activityData.activity?.score ?? null;
        loading = activityData.loading;
        error = activityData.error ? new Error(activityData.error) : null;
        break;
      }
      case "oura_sleep_score": {
        currentValue = sleepData.sleep?.score ?? null;
        loading = sleepData.loading;
        error = sleepData.error ? new Error(sleepData.error) : null;
        break;
      }
      case "oura_steps": {
        currentValue = activityData.activity?.steps ?? null;
        loading = activityData.loading;
        error = activityData.error ? new Error(activityData.error) : null;
        break;
      }
    }
  }

  return {
    error,
    indicator,
    loading,
    value: currentValue,
  };
}
